import {
  Link,
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIcon,
  BoxesIcon,
  EyeOffIcon,
  FileIcon,
  ScanSearchIcon,
  SunIcon,
} from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { FileScanAction, FileScanType } from "@/lib/http/pii-file-scan";
import { documentRedactorState } from "@/lib/document-redactor-state";
import { useSet } from "@/lib/hooks";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { allEntitiesQueryOptions } from "@/lib/queries/entities";
import { LoadingSpinner } from "@/components/loading";
import { FilePreview } from "@/components/filePreview";
import { SubmitterText } from "@/components/ui/submit-button";
import { runFileScan } from "@/lib/queries/pii-file-scan";

export const Route = createFileRoute("/_app/document-redactor/options")({
  component: RouteComponent,
  beforeLoad() {
    if (!documentRedactorState.getFile())
      throw redirect({ to: "/document-redactor" });
  },
  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(allEntitiesQueryOptions());
  },
});

type RedactionScope = "regular" | "contextual";

function RouteComponent() {
  const file = documentRedactorState.getFile();

  const entities = useQuery({
    ...allEntitiesQueryOptions(),
    select(data) {
      return data.map((entity) => entity.name);
    },
  });

  const selectedEntities = useSet<string>();
  useEffect(() => {
    if (entities.isSuccess) {
      selectedEntities.clear();
      entities.data.forEach((name) => selectedEntities.add(name));
    }
  }, [entities.isSuccess]);

  const allEntitiesSelected = Boolean(
    entities.data &&
      entities.data.length === selectedEntities.size &&
      entities.data.every((name) => selectedEntities.has(name)),
  );

  const togglePii = useCallback(
    (pii: string) => {
      selectedEntities.has(pii)
        ? selectedEntities.delete(pii)
        : selectedEntities.add(pii);
    },
    [selectedEntities],
  );

  const [scanType, setScanType] = useState<FileScanType>("annotate");
  const [redactionType, setRedactionType] = useState<FileScanAction>("redact");
  const [redactionScope, setRedactionScope] =
    useState<RedactionScope>("regular");
  const [aggregateEntities, setAggregateEntities] = useState(true);

  const [searchEntities, setSearchEntities] = useState("");
  const [showMore, setShowMore] = useState(false);
  const foundEntities = (entities.data ?? []).filter((name) =>
    name.toLowerCase().includes(searchEntities.toLowerCase()),
  );
  const hasMoreEntities = foundEntities.length > 10;
  const entitiesToShow = showMore ? foundEntities : foundEntities.slice(0, 10);

  const navigate = Route.useNavigate();
  const router = useRouter();
  const scanFile = useMutation({
    mutationFn: runFileScan,
    onMutate() {
      router.preloadRoute({ from: Route.fullPath, to: "../result" });
    },
    onSuccess(data) {
      documentRedactorState.setResult(data);
      navigate({
        from: Route.fullPath,
        to: "../result",
        mask: {
          from: Route.fullPath,
          to: "..",
        },
      });
    },
  });

  if (!file) return null;

  const contextual = redactionScope === "contextual";
  const submitLabel =
    scanType === "extract"
      ? "Extract"
      : redactionType === "highlight"
        ? "Highlight"
        : "Redact";

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 lg:flex-row">
      <div className="flex-1">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Document Redactor</h1>
            <p className="text-muted-foreground">
              Highlights PIIs on your files
            </p>
          </div>
          <Link
            className={cn(buttonVariants({ variant: "outline" }), "gap-2")}
            to=".."
            replace
            onClick={() => documentRedactorState.clear()}
          >
            <FileIcon className="h-4 w-4" />
            Start new
          </Link>
        </div>

        <Card className="relative flex h-90 w-full items-center justify-center overflow-clip border-dashed bg-foreground/10 p-2 has-data-previewing:h-180">
          <FilePreview file={file} />
        </Card>
      </div>

      <div className="w-full lg:w-[400px]">
        <h2 className="mb-6 text-xl font-semibold">
          Configure redaction settings
        </h2>

        <div className="space-y-6">
          <div>
            <label className="mb-2 block font-medium">
              Scan type<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <Button
                className="flex-1 justify-start gap-2"
                variant={scanType === "annotate" ? "default" : "outline"}
                onClick={() => setScanType("annotate")}
              >
                <EyeOffIcon className="h-4 w-4" />
                Redact file
              </Button>
              <Button
                className="flex-1 justify-start gap-2"
                variant={scanType === "extract" ? "default" : "outline"}
                onClick={() => setScanType("extract")}
              >
                <ScanSearchIcon className="h-4 w-4" />
                Extract PII
              </Button>
            </div>
          </div>

          {scanType === "annotate" ? (
            <div>
              <label className="mb-2 block font-medium">
                Redaction type<span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Button
                  className="flex-1 justify-start gap-2"
                  variant={
                    redactionType === "highlight" ? "default" : "outline"
                  }
                  onClick={() => setRedactionType("highlight")}
                >
                  <SunIcon className="h-4 w-4" />
                  Highlight PIIs
                </Button>
                <Button
                  className="flex-1 justify-start gap-2"
                  variant={redactionType === "redact" ? "default" : "outline"}
                  onClick={() => setRedactionType("redact")}
                >
                  <EyeOffIcon className="h-4 w-4" />
                  Mask PIIs
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <Label htmlFor="aggregate-entities" className="font-medium">
                Aggregate entities
              </Label>
              <Switch
                id="aggregate-entities"
                checked={aggregateEntities}
                onCheckedChange={setAggregateEntities}
              />
            </div>
          )}

          <div>
            <label className="mb-2 block font-medium">
              Detection scope<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <Button
                className="flex-1 justify-start gap-2"
                variant={redactionScope === "regular" ? "default" : "outline"}
                onClick={() => setRedactionScope("regular")}
              >
                <ActivityIcon className="h-4 w-4" />
                Regular
              </Button>
              <Button
                className="flex-1 justify-start gap-2"
                variant={
                  redactionScope === "contextual" ? "default" : "outline"
                }
                onClick={() => setRedactionScope("contextual")}
              >
                <BoxesIcon className="h-4 w-4" />
                Contextual
              </Button>
            </div>
          </div>

          <div>
            <label className="mb-2 block font-medium">
              Select PIIs<span className="text-red-500">*</span>
            </label>

            <Input
              value={searchEntities}
              onChange={(e) => {
                setSearchEntities(e.target.value);
                setShowMore(false);
              }}
              type="search"
              placeholder="Search"
              className="my-2"
            />

            {entities.isSuccess ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (allEntitiesSelected) selectedEntities.clear();
                    else
                      entities.data.forEach((name) =>
                        selectedEntities.add(name),
                      );
                  }}
                >
                  {!allEntitiesSelected ? "Select All" : "Deselect All"}
                </Button>

                <div className="mt-4 flex flex-wrap gap-2">
                  {entitiesToShow.map((pii) => (
                    <Button
                      key={pii}
                      variant="outline"
                      size="sm"
                      onClick={() => togglePii(pii)}
                      data-selected={
                        selectedEntities.has(pii) ? "true" : undefined
                      }
                      className={cn(
                        "rounded-full text-sm",
                        "data-selected:bg-gray-900 data-selected:text-white",
                        "dark:data-selected:bg-gray-100 dark:data-selected:text-black",
                      )}
                    >
                      {pii}
                    </Button>
                  ))}
                </div>

                {hasMoreEntities && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowMore(!showMore)}
                    className="mt-1 px-1.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    {showMore ? "Show Less" : "Show More"}
                  </Button>
                )}
              </>
            ) : entities.isLoading ? (
              <LoadingSpinner className="my-5" />
            ) : (
              "Error!"
            )}
          </div>
        </div>

        <div className="mt-8">
          <Button
            className="w-full"
            size="lg"
            onClick={() =>
              scanFile.mutate({
                file,
                scan_type: scanType,
                entity_list: [...selectedEntities],
                action: redactionType,
                aggregate_entities: aggregateEntities,
                detect_pid: contextual,
                detect_signature: scanType === "annotate" && contextual,
                detect_fingerprint: scanType === "annotate" && contextual,
              })
            }
            disabled={selectedEntities.size < 1 || scanFile.isPending}
          >
            <SubmitterText
              submitting={scanFile.isPending}
              submittingChildren={
                scanType === "extract" ? "Extracting..." : "Redacting..."
              }
            >
              {selectedEntities.size < 1
                ? "Please select at least one entity"
                : submitLabel}
            </SubmitterText>
          </Button>
          {scanFile.isError && (
            <span className="text-destructive">{scanFile.error.message}</span>
          )}
        </div>
      </div>
    </div>
  );
}
